import React from 'react';
import { motion } from 'framer-motion';
import {
  GlobeAltIcon,
  UserGroupIcon,
  SparklesIcon,
  RocketLaunchIcon,
  BuildingLibraryIcon,
  PresentationChartBarIcon,
  UserPlusIcon,
  LightBulbIcon,
  LanguageIcon
} from '@heroicons/react/24/outline';

const About: React.FC = () => {
  const sections = [
    {
      id: 'what',
      question: 'What is the LANGUAGE INTELLIGENCE conference?',
      answer: "It's a premier event that explores how AI-powered language technologies are transforming industries through innovations in translation, interpretation, text & speech processing, and data management.",
      icon: GlobeAltIcon
    },
    {
      id: 'who',
      question: 'Who are the key speakers and attendees?',
      answer: 'Leaders in language intelligence, industry technologists, market analysts, and research experts who showcase real-world use cases from sectors like automotive, healthcare, media, and more.',
      icon: UserGroupIcon
    },
    {
      id: 'expect',
      question: 'What can participants expect?',
      answer: [
        {
          title: 'Innovative Presentations',
          description: 'Insights into cutting-edge developments and practical case studies.',
          icon: PresentationChartBarIcon
        },
        {
          title: 'Networking Opportunities',
          description: 'Engagement with top technologists, business leaders, and industry experts.',
          icon: UserPlusIcon
        },
        {
          title: 'Strategic Insights',
          description: 'Real-world examples of how multilingual AI boosts customer experience and operational excellence.',
          icon: LightBulbIcon
        }
      ],
      icon: SparklesIcon
    },
    {
      id: 'why',
      question: 'Why should I attend?',
      answer: 'Attending the conference offers a unique chance to stay ahead of industry trends, learn from renowned experts, forge valuable partnerships, and gain actionable strategies for integrating advanced language AI into your business.',
      icon: RocketLaunchIcon
    },
    {
      id: 'where',
      question: 'Where is the event held?',
      answer: 'In the heart of Austria\'s capital at the prestigious Austrian Parliament, providing an inspiring backdrop for professional exchange.',
      icon: BuildingLibraryIcon
    }
  ];

  return (
    <div className="max-w-5xl mx-auto">
      {/* Hero Section */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="text-center mb-20 pt-4"
      >
        <h1 className="text-5xl font-bold bg-gradient-to-r from-[#00529B] to-[#E31D3C] bg-clip-text text-transparent mb-8 leading-relaxed pb-2">
          Shaping the Future of Language Technology
        </h1>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-4">
          Join us for an extraordinary gathering of minds at the intersection of AI and language technology
        </p>
      </motion.div>

      {/* Definition Callout */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.3 }}
        className="mb-16 bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 shadow-lg border border-gray-100"
      >
        <div className="flex items-start gap-6 max-w-4xl mx-auto">
          <LanguageIcon className="w-8 h-8 text-[#00529B] flex-shrink-0 mt-1" />
          <div>
            <h2 className="text-2xl font-semibold mb-4 text-[#00529B]">What is Language Intelligence?</h2>
            <p className="text-gray-700 leading-relaxed">
              Language intelligence is the ability to process and use language effectively in different situations. 
              It means not only understanding and producing speech and writing but also expressing ideas, emotions, 
              and cultural nuances. Beyond basic language skills, it covers social, emotional, and functional 
              communication. Today, language intelligence is key to developing secure, accurate, and user-friendly 
              AI and NLP applications that enable natural interactions between humans and machines.
            </p>
          </div>
        </div>
      </motion.div>

      {/* Main Content */}
      <div className="grid gap-8">
        {sections.map((section, index) => {
          const Icon = section.icon;
          return (
            <motion.div
              key={section.id}
              initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 p-6"
            >
              <div className="flex items-center gap-4 mb-6">
                <Icon className="w-8 h-8 text-[#00529B]" />
                <h2 className="text-2xl font-semibold text-gray-800">{section.question}</h2>
              </div>
              
              {Array.isArray(section.answer) ? (
                <div className="grid md:grid-cols-3 gap-6 mt-6">
                  {section.answer.map((item, idx) => {
                    const ItemIcon = item.icon;
                    return (
                      <div 
                        key={idx}
                        className="bg-gray-50 rounded-xl p-6 transform hover:-translate-y-1 transition-transform duration-300"
                      >
                        <ItemIcon className="w-8 h-8 text-[#00529B] mb-4" />
                        <h3 className="font-semibold text-lg mb-2 text-[#00529B]">{item.title}</h3>
                        <p className="text-gray-600">{item.description}</p>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-gray-600 leading-relaxed pl-12">{section.answer}</p>
              )}
            </motion.div>
          );
        })}
      </div>

      {/* Call to Action */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.8 }}
        className="mt-16 text-center bg-gradient-to-r from-[#00529B] to-[#E31D3C] rounded-2xl p-8 text-white"
      >
        <h2 className="text-3xl font-bold mb-4">Ready to Join Us?</h2>
        <p className="text-lg mb-6">Be part of the revolution in language technology</p>
        <button 
          className="bg-white text-[#00529B] px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors duration-300"
          onClick={() => window.open('https://www.eventbrite.co.uk/e/language-intelligence-2025-tickets-1299678576609', '_blank', 'noopener,noreferrer')}
        >
          Register Now
        </button>
      </motion.div>
    </div>
  );
};

export default About;
